import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Search, Layers3, Package, Warehouse as WarehouseIcon, Check, Sticker } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
/**
 * Unified stock view — renders one of three ledgers based on `variant`:
 *   - semi           → /production/semi-stocks
 *   - preqc          → /production/finished-preqc-stocks
 *   - warehouse      → /production/warehouse-stocks (grouped by product × warehouse; adds inline HSN edit)
 */

const CONFIG = {
  semi: {
    title: "Semi-Finished Stock",
    subtitle: "Raw-material-wise stock after Level 1 fabrication, before plating",
    endpoint: "/production/semi-stocks",
    icon: Layers3,
    columns: ["Raw Material", "Code", "Qty", "Last Movement"],
    testidPrefix: "semi-stock",
  },
  preqc: {
    title: "Finished (Pre-QC) Stock",
    subtitle: "Plated pieces awaiting Quality Control",
    endpoint: "/production/finished-preqc-stocks",
    icon: Package,
    columns: ["Product", "SKU", "Qty", "Last Movement"],
    testidPrefix: "preqc-stock",
  },
  warehouse: {
    title: "Warehouse Stock",
    subtitle: "QC-passed finished goods per warehouse location — HSN editable, thermal barcode stickers reprintable",
    endpoint: "/production/warehouse-stocks",
    icon: WarehouseIcon,
    columns: ["Product", "SKU", "HSN", "Warehouse", "Qty", "Stickers", "Last Movement"],
    testidPrefix: "wh-stock",
  },
};

export default function StockView({ variant }) {
  const cfg = CONFIG[variant];
  const Icon = cfg.icon;
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [editing, setEditing] = useState({}); // { product_id: hsn_string }
  const [selected, setSelected] = useState({}); // { product_id: qty (editable) }

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (variant === "warehouse" && warehouseFilter) params.warehouse_id = warehouseFilter;
      const r = await api.get(cfg.endpoint, { params });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [cfg.endpoint, search, warehouseFilter, variant]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (variant !== "warehouse") return;
    api.get("/production/warehouses").then((r) => setWarehouses(r.data || []));
  }, [variant]);

  const saveHsn = async (productId, hsnCode) => {
    try {
      await api.patch(`/sales/products/${productId}/hsn`, { hsn_code: hsnCode || null });
      setRows((arr) => arr.map((r) => r.product_id === productId ? { ...r, hsn_code: hsnCode || null } : r));
      setEditing((e) => { const c = { ...e }; delete c[productId]; return c; });
      toast.success("HSN saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4">
      <PageSection title={cfg.title} description={cfg.subtitle}>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product name/sku..." className="pl-8" data-testid={`${cfg.testidPrefix}-search`} />
            </div>
            {variant === "warehouse" && (
              <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="wh-stock-filter">
                <option value="">All warehouses</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            )}
            {variant === "warehouse" && Object.keys(selected).length > 0 && (
              <button
                onClick={() => {
                  const items = Object.entries(selected)
                    .filter(([, q]) => Number(q) > 0)
                    .map(([pid, q]) => ({ product_id: pid, qty: Number(q), ref_type: "warehouse_reprint" }));
                  if (!items.length) return;
                  const key = `bulk-${Date.now()}`;
                  sessionStorage.setItem(key, JSON.stringify(items));
                  window.open(`/production/sticker-print?bulk=${key}`, "_blank", "noopener");
                }}
                className="h-9 px-3 text-xs rounded bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white font-semibold inline-flex items-center gap-1"
                data-testid="wh-bulk-print"
              >
                <Sticker className="h-3.5 w-3.5" /> Print {Object.keys(selected).length} selected
              </button>
            )}
            <span className="text-xs text-gray-500 ml-auto">{rows.length} row(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                {variant === "warehouse" && <th className="px-3 py-2 w-8"></th>}
                {cfg.columns.map((c) => <th key={c} className={`px-3 py-2 ${c === "Qty" ? "text-right" : ""}`}>{c}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr><td colSpan={cfg.columns.length + (variant === "warehouse" ? 1 : 0)} className="px-3 py-10 text-center text-gray-400">
                  <Icon className="h-8 w-8 mx-auto mb-2 opacity-30" /> No stock yet.
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={`${r.raw_material_id || r.product_id}-${r.warehouse_id || i}`} className="hover:bg-gray-50" data-testid={`${cfg.testidPrefix}-row-${r.raw_material_id || r.product_id}`}>
                  {variant === "warehouse" && (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected[r.product_id] !== undefined}
                        onChange={(e) => setSelected((s) => {
                          const c = { ...s };
                          if (e.target.checked) c[r.product_id] = Math.max(1, Math.round(Number(r.qty) || 1));
                          else delete c[r.product_id];
                          return c;
                        })}
                        data-testid={`wh-select-${r.product_id}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium">
                    {variant === "semi"
                      ? (r.raw_material_name || r.product_name || "—")
                      : r.product_name}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">
                    {variant === "semi"
                      ? (r.unit || r.product_sku || "—")
                      : (r.product_sku || "—")}
                  </td>
                  {variant === "warehouse" && (
                    <td className="px-3 py-2">
                      {editing[r.product_id] !== undefined ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={editing[r.product_id]}
                            onChange={(e) => setEditing((s) => ({ ...s, [r.product_id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") saveHsn(r.product_id, editing[r.product_id]); if (e.key === "Escape") setEditing((s) => { const c = { ...s }; delete c[r.product_id]; return c; }); }}
                            className="h-7 w-24 rounded border border-gray-300 px-2 text-xs font-mono"
                            autoFocus
                            data-testid={`wh-hsn-input-${r.product_id}`}
                          />
                          <button onClick={() => saveHsn(r.product_id, editing[r.product_id])} className="h-7 w-7 rounded bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center" data-testid={`wh-hsn-save-${r.product_id}`}><Check className="h-3.5 w-3.5 text-emerald-700" /></button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditing((s) => ({ ...s, [r.product_id]: r.hsn_code || "" }))}
                          className="font-mono text-xs text-left hover:bg-blue-50 rounded px-2 py-0.5 border border-transparent hover:border-blue-200 min-w-[60px]"
                          data-testid={`wh-hsn-edit-${r.product_id}`}
                          title="Click to edit HSN"
                        >
                          {r.hsn_code || <span className="text-gray-400 italic">set HSN</span>}
                        </button>
                      )}
                    </td>
                  )}
                  {variant === "warehouse" && <td className="px-3 py-2 text-gray-700">{r.warehouse_name || "—"}</td>}
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.qty}</td>
                  {variant === "warehouse" && (
                    <td className="px-3 py-2">
                      <button
                        onClick={() => {
                          const params = new URLSearchParams({
                            product_id: r.product_id,
                            qty: String(Math.max(1, Math.round(Number(r.qty) || 1))),
                            ref_type: "warehouse_reprint",
                          });
                          window.open(`/production/sticker-print?${params.toString()}`, "_blank", "noopener");
                        }}
                        className="h-7 px-2 text-xs rounded bg-white hover:bg-red-50 text-[#9B1C1C] font-semibold inline-flex items-center gap-1 border border-red-200"
                        data-testid={`wh-sticker-${r.product_id}`}
                        title="Reprint barcode stickers for this product"
                      >
                        <Sticker className="h-3 w-3" /> Print
                      </button>
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs text-gray-500">{formatDateTime(r.last_movement_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>
    </div>
  );
}

// Route entry-points
export function SemiStockPage() { return <StockView variant="semi" />; }
export function PreQCStockPage() { return <StockView variant="preqc" />; }
export function WarehouseStockPage() { return <StockView variant="warehouse" />; }
